import React from 'react';
import { logger } from '../../utils/logger';

type Props = {
  children: React.ReactNode;
  /** Short label used in the fallback UI, e.g. "preview" or "editor". */
  label?: string;
  /** Optional custom fallback. Receives the error and a reset() callback. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  /** Called whenever a child throws, useful for logging to a monitoring service. */
  onError?: (error: Error, info: React.ErrorInfo) => void;
};

type State = {
  error: Error | null;
};

/**
 * Generic error boundary. Wrap any pane that renders dynamic/untrusted CMS
 * content (live preview, section editors) so a single broken section can't
 * take down the whole admin dashboard.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error(`[ErrorBoundary${this.props.label ? `:${this.props.label}` : ''}]`, error, info);
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      return (
        <div
          role="alert"
          className="flex flex-col items-center justify-center h-full w-full p-8 text-center bg-red-50 border border-red-200 rounded"
        >
          <h3 className="text-base font-semibold text-red-700 mb-1">
            {this.props.label ? `Something went wrong in the ${this.props.label}.` : 'Something went wrong.'}
          </h3>
          <p className="text-sm text-red-600 mb-4 max-w-md">
            {error.message || 'An unexpected error occurred while rendering this section.'}
          </p>
          <button
            onClick={this.reset}
            className="text-sm px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
